import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  List,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { colorOverrides as colors } from "theme/colorOverrides";
import { DOCS_TREE_LIST_SX, DocsTreeItem } from "./components/DocsTreeItem";
import { docsManifest, filterDocs } from "./docsManifest";
import { DOCS_ROW_INSET } from "./docsStyles";

interface DocsSidebarProps {
  activeSlug: string;
  /** Called when a doc is picked, so the mobile drawer can close. */
  onNavigate?: () => void;
}

// The slug and every ancestor slug, so the active page's branch starts open.
const ancestorSlugs = (slug: string) =>
  slug.split("/").map((_, i, parts) => parts.slice(0, i + 1).join("/"));

export const DocsSidebar = ({ activeSlug, onNavigate }: DocsSidebarProps) => {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(ancestorSlugs(activeSlug))
  );

  useEffect(() => {
    setExpanded(
      (prev) => new Set(Array.from(prev).concat(ancestorSlugs(activeSlug)))
    );
  }, [activeSlug]);

  const entries = useMemo(() => filterDocs(docsManifest, query), [query]);
  const searching = query.trim().length > 0;

  const toggle = (slug: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });

  return (
    <Box
      component="nav"
      aria-label="Docs"
      // App.css sizes every <nav> for the top bar (height: 40px; padding: 10px).
      sx={{ height: "auto", padding: 0 }}
    >
      <Typography
        component="h2"
        sx={{
          px: `${DOCS_ROW_INSET}px`,
          mb: 3,
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: colors.gray07,
        }}
      >
        Docs
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Search docs"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        inputProps={{ "aria-label": "Search docs" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            backgroundColor: "#fff",
          },
          // Below 16px, iOS Safari zooms the page when the input is focused.
          "& input": { fontSize: { xs: 16, md: 14 } },
        }}
      />
      {entries.length > 0 ? (
        <List disablePadding sx={DOCS_TREE_LIST_SX}>
          {entries.map((entry) => (
            <DocsTreeItem
              key={entry.slug}
              entry={entry}
              depth={0}
              activeSlug={activeSlug}
              expanded={expanded}
              searching={searching}
              onToggle={toggle}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      ) : (
        <Typography
          sx={{
            px: `${DOCS_ROW_INSET}px`,
            py: 2,
            fontSize: 14,
            color: colors.gray07,
          }}
        >
          No docs match "{query.trim()}"
        </Typography>
      )}
    </Box>
  );
};
